import { DefaultPromptObjectMapping, ExpandedFeaturesInterface, IPromptFormData, ModelPerformanceInterface, promptObjectListItem, SupportedFeaturesInterface } from "../interfaces/prompts/prompts.interface";


// Prompt execution status constants
export const ASSET_EXECUTION_STATUS = {
  NOT_TRIGGERED: -1,
  QUEUED: 0,
  IN_PROGRESS: 1,
  COMPLETED: 2,
  PENDING_USER_INPUT: 3,
  PAUSED: 4,
  TERMINATED: 5,
  FAILED: 6,
  SKIPPED: 8,
} as const;

// Prompt type constants
export const PROMPT_TYPE = {
  STANDARD_PROMPT: 1,
  CHART_PROMPT: 2,
  CONTENT_UPLOAD: 4,
  AGENT_VARIABLE_DEFINITION: 5,
  ITERATIVE_PROMPT: 6,
  INPUT_DUAL_PROMPT: 7,
  SURVEY_PROMPT: 8,
  LOOP_PROMPT: 9,
  HYBRID_PROMPT: 10,
  RESPONSE_COMPILATION_PROMPT: 11,
  FLIGHT_VARIABLE_DEFINITION: 12,
} as const;

// Character count constants for prompts
export const PROMPT_CHAR_COUNT = {
  DUAL_PROMPT_REFERENCE_DATA: 300000,
  USER_PROMPT: 500,
  PROMPT_DESCRIPTION: 1000,
  PROMPT_NAME: 45,
  PROMPT_DISPLAY_NAME: 45,
  PROMPT_INSTRUCTION: 500,
  DEFAULT_ACTION: 300000,
  AGENT_VARIABLE_DEFINITION: 300000,
  FLIGHT_VARIABLE_DEFINITION: 300000,
  GAIN_MIN_VALUE: 0,
  GAIN_MAX_VALUE: 3000,
  INSTRUCTIONTOUSER: 2500,
  INSTRUCTION_FOR_INSTANCE_SETUP: 2500,
  ACTOR_DEFINITION: 2500,
  ENVIRONMENT_CONTEXT: 50000,
  CHALLENGE_DESCRIPTION: 50000,
  DATA_HANDLING: 300000,
  OBJECTIVE: 50000,
  AUDIENCE: 2500,
  TASK: 50000,
  SURVEY_CONTENT: 2500,
  ITERATIVE_PRE_PROMPT_TASKS: 50000,
  ITERATIVE_POST_PROMPT_FORMAT: 50000,
  INSTRUCTION_FOR_AUTOTRIGGER: 300000, 
  WRITING_STYLE: 2500,
  TIMELINE_PRIORITY: 2500,
  OUTPUT_FORMAT: 50000,
  PROMPT_REFERENCES: 2500,
  VARIABLE_DEFINITION_PROMPT: 300000,
  VARIABLE_DEFINITION_AGENT: 300000,
  VARIABLE_DEFINITION_SOLUTION: 300000,
  VARIABLE_DEFINITION_CORPORATE: 2500,
  PROCESS_ACTION: 2500,
  COVERPAGE_IMAGE_URL: 250,
  BACKPAGE_IMAGE_URL: 250,
  STRUCTURED_OUTPUT_FORMAT: 10000,
  CHAT_INSTRUCTIONS: 2500,
  EXPOSE_OUTPUT_CUSTOM_MESSAGE: 500,
  // Add more character count constants as needed
} as const;

export const getPromptCharacterCountConfigs = {
  PROMPTNAME: PROMPT_CHAR_COUNT.PROMPT_NAME,
  PROMPTDISPLAYNAME: PROMPT_CHAR_COUNT.PROMPT_DISPLAY_NAME,
  PROMPTDESCRIPTION: PROMPT_CHAR_COUNT.PROMPT_DESCRIPTION,
  INSTRUCTIONTOUSER: PROMPT_CHAR_COUNT.INSTRUCTIONTOUSER,
  INSTRUCTIONFORINSTANCESETUP: PROMPT_CHAR_COUNT.INSTRUCTION_FOR_INSTANCE_SETUP,
  LLMSTRUCTUREDOUTPUTFORMAT: PROMPT_CHAR_COUNT.STRUCTURED_OUTPUT_FORMAT,
  EXPOSEOUTPUTCUSTOMMESSAGE: PROMPT_CHAR_COUNT.EXPOSE_OUTPUT_CUSTOM_MESSAGE,
};

// Reference data type constants
export const REFERENCE_DATA_TYPE = {
  INPUT: 0,
  FILE_UPLOAD: 1,
} as const;

// Prompt trigger type constants
export const PROMPT_TRIGGER_TYPE = {
  HUMAN: 'Human',
  AUTONOMOUS: 'Autonomous',
} as const;

// Prompt DNA constants
export const PROMPT_DNA = {
  HUMAN_TRIGGER: 'H',
  AUTONOMOUS_TRIGGER: 'P',
} as const;

// Prompt status messages
type AssetExecutionStatus = typeof ASSET_EXECUTION_STATUS[keyof typeof ASSET_EXECUTION_STATUS];
export const ASSET_EXECUTION_STATUS_MESSAGES = {
  [ASSET_EXECUTION_STATUS.NOT_TRIGGERED]: 'assetExecutionStatus.notTriggered.message',//'Asset execution is not triggered yet',
  [ASSET_EXECUTION_STATUS.QUEUED]: 'assetExecutionStatus.queued.message',//'Asset is added to execution queue',
  [ASSET_EXECUTION_STATUS.IN_PROGRESS]: 'assetExecutionStatus.inProgress.message',//'Asset Execution is In progress',
  [ASSET_EXECUTION_STATUS.COMPLETED]: 'assetExecutionStatus.completed.message',//'Asset Execution is completed',
  [ASSET_EXECUTION_STATUS.PENDING_USER_INPUT]: 'assetExecutionStatus.pendingUserInput.message',//'Asset Execution is pending with user input. Step may require user input to proceed',
  [ASSET_EXECUTION_STATUS.PAUSED]: 'assetExecutionStatus.paused.message',//'Paused the Asset execution',
  [ASSET_EXECUTION_STATUS.TERMINATED]: 'assetExecutionStatus.terminated.message',//'Asset Execution terminated',
  [ASSET_EXECUTION_STATUS.FAILED]: 'assetExecutionStatus.failed.message',//'Asset Execution is failed',
  [ASSET_EXECUTION_STATUS.SKIPPED]: 'assetExecutionStatus.skipped.message',//'Skipped execution e.g. event trigger',
} as const satisfies Record<AssetExecutionStatus, string>;

// Prompt validation constants
export const PROMPT_VALIDATION = {
  GAIN_MIN_VALUE: 0,
  GAIN_MAX_VALUE: 3000,
  MIN_NAME_LENGTH: 3,
  MAX_NAME_LENGTH: 100,
  MIN_DESCRIPTION_LENGTH: 10,
  MAX_DESCRIPTION_LENGTH: 500,
  REQUIRED_FIELDS: ['PROMPTNAME', 'PROMPTDESCRIPTION'],
  MAX_RATING: 7,
} as const;

export const STAR_RATING_MESSAGES = [
  { color: 'var(--bs-gray)', tooltipKey: 'ratingStar1.tooltip' },
  { color: 'var(--bs-primary)', tooltipKey: 'ratingStar2.tooltip' },
  { color: 'var(--bs-primary)', tooltipKey: 'ratingStar3.tooltip' },
  { color: 'var(--bs-primary)', tooltipKey: 'ratingStar4.tooltip' },
  { color: 'var(--bs-primary)', tooltipKey: 'ratingStar5.tooltip' },
  { color: 'var(--bs-primary)', tooltipKey: 'ratingStar6.tooltip' },
  { color: 'var(--bs-secondary)', tooltipKey: 'ratingStar7.tooltip' }
];

export const interactionTypeMapping = [
  { interactionType: 1, supportedLevels: [0, 1, 2] },
  { interactionType: 2, supportedLevels: [0, 1, 2] },
  { interactionType: 4, supportedLevels: [2] },
  { interactionType: 5, supportedLevels: [0, 1, 2] },
  { interactionType: 6, supportedLevels: [2] },
  { interactionType: 7, supportedLevels: [2] },
  { interactionType: 8, supportedLevels: [2] },
  { interactionType: 9, supportedLevels: [0, 1, 2] },
  { interactionType: 10, supportedLevels: [1, 2] },
  { interactionType: 11, supportedLevels: [0, 1, 2] },
  { interactionType: 12, supportedLevels: [0, 1, 2] }
];

export const promptObjectList: promptObjectListItem[] = [
  {
      id: 'promptActorDefinition',
      name: 'actorPersona.text',
      level: 'S',
      interaction: [1, 2, 6, 7, 8, 9, 10, 11],
      key: "ACTORDEFINITION",
      required: true
  },
  {
      id: 'promptEnvContext',
      name: 'environmentContext.text',
      level: 'S',
      interaction: [1, 2, 6, 7, 8, 9, 10, 11],
      key: "ENVIRONMENTCONTEXT",
      required: true
  },
  {
      id: 'promptDataHandling',
      name: 'dataHandling.text',
      level: 'E',
      interaction: [1, 2, 6, 7, 8, 9, 10, 11],
      key: "DATAHANDLING",
      required: false
  },
  {
      id: 'promptGptblueObj',
      name: 'objective.text',
      level: 'A',
      interaction: [1, 2, 6, 7, 8, 9, 10, 11],
      key: "OBJECTIVE",
      required: true
  },
  {
      id: 'promptAudience',
      name: 'audience.text',
      level: 'A',
      interaction: [1, 2, 6, 7, 8, 9, 10, 11],
      required: false
  },
  {
      id: 'promptTask',
      name: 'task.text',
      level: 'S',
      interaction: [1, 2, 7, 9, 10, 11],
      required: false
  },
  {
      id: 'promptRQuery',
      name: 'iterativeTask.text',
      level: 'E',
      interaction: [6],
      required: false
  },
  {
      id: 'promptSurveyPrompt',
      name: 'surveyTask.text',
      level: 'E',
      interaction: [8],
      required: false
  },
  {
      id: 'promptDAction',
      name: 'defaultAction.text',
      level: 'S',
      interaction: [1, 2, 4, 5, 6, 7, 8, 9, 10, 12],
      key: "DEFAULTACTION",
      required: true
  },
  {
      id: 'promptWritingStyle',
      name: 'writingStyle.text',
      level: 'A',
      interaction: [1, 2, 6, 7, 8, 9, 10, 11],
      required: false
  },
  {
      id: 'promptProcessAction',
      name: 'processAction.text',
      level: 'S',
      interaction: [1, 2, 4, 6, 7, 8, 9, 10, 11],
      required: false
  },
  {
      id: 'promptOFormat',
      name: 'outputFormat.text',
      level: 'A',
      interaction: [1, 2, 6, 7, 8, 9, 10, 11],
      required: false
  },
  {
      id: 'promptReferences',
      name: 'promptReferences.text',
      level: 'A',
      interaction: [1, 2, 6, 7, 8, 9, 10, 11],
      required: false
  },
  {
      id: 'promptFunctionCall',
      name: 'functionCall.text',
      level: 'E',
      interaction: [1, 2, 6, 7, 8, 10, 11],
      required: false
  },
  {
      id: 'promptCustomModel',
      name: 'customModel.text',
      level: 'A',
      interaction: [1, 2, 6, 8, 10],
      required: false
  },
  {
      id: 'autonomousTrigger',
      name: 'autonomousTrigger.text',
      level: 'E',
      interaction: [1, 2, 4, 6, 7, 8, 9, 10, 11],
      required: false
  },
  {
    id: 'promptEnableChat',
    name: 'promptChatObject.text',
    level: 'E',
    interaction: [1, 2, 6, 7, 8, 9, 10, 11],
    required: false
  }
];

export const initialModelPerformance: ModelPerformanceInterface = {
  responseDetail: 0, // 0: low
  reasoningDepth: 0, // 0: minimal
};

export const RESPONSE_DETAIL = {
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
} as const;

export const REASONING_DEPTH = {
  MINIMAL: "minimal",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
} as const;

export const promptActionTools = [
  { id: "web_search", label: "Web Search", desc: "Fetch current info during a run." },
  { id: "code_interpreter", label: "Code Interpreter", desc: "Run code in a sandbox." },
];

export const initialSupportedFeatures: SupportedFeaturesInterface = {
  isModelPerformanceSupported: false,
  isActionToolsSupported: false,
  isStructuredOutputSupported: false,
};

export const initialExpandedFeatures: ExpandedFeaturesInterface = {
  isModelPickerExpanded: false,
  isModelPerformanceExpanded: false,
  isActionToolsExpanded: false,
  isStructuredOutputExpanded: false,
};


export const promptFormDataKeys: (keyof IPromptFormData)[] = [
  "GPTBLUEPROMPTID",
  "PROMPTNAME",
  "PROMPTDISPLAYNAME",
  "PROMPTDESCRIPTION",
  "AUTOSAVE",
  "AUTHORVIEW",
  "ALLOWTOCOPY",
  "CUSTOMDATA",
  "FUNCTIONCALL",
  "SPONSOREDPROMPT",
  "ITERATIVETASK",
  "PUBLICACCESSIBILITY",
  "PROMPTLEVEL",
  "OPENSOURCE",
  "promptImage",
  "CUSTOMMODELID",
  "PREMIUMPRICECC",
  "SEGMENTARR",
  "referencedPromptArr",
  "SURVEYPROMPT",
  "INSTRUCTIONTOUSER",
  "EXPIREON",
  "ISEXPIRYPROMPT",
  "DUALPROMPT",
  "AGENTREADY",
  "LLMMODELID",
  "ALLOWSOCIALSHARE",
  "PROMPTTYPEID",
  "AUTONOMOUSTRIGGER",
  "COVERPAGEIMAGEURL",
  "BACKPAGEIMAGEURL",
  "INSTRUCTIONFORAUTOTRIGGER",
  "DEFAULTACTION",
  "ACTORDEFINITION",
  "ENVIRONMENTCONTEXT",
  "CHALLENGEDESCRIPTION",
  "DATAHANDLING",
  "OBJECTIVE",
  "AUDIENCE",
  "TASK",
  "ITERATIVEPREPROMPTTASKS",
  "ITERATIVEPOSTPROMPTFORMAT",
  "WRITINGSTYLE",
  "TIMELINEPRIORITY",
  "OUTPUTFORMAT",
  "PROMPTREFERENCES",
  "FUNCTIONID",
  "SURVEYCONTENT",
  "DUALPROMPTREFERENCEDATATYPE",
  "DUALPROMPTREFERENCEDATA",
  "DUALPROMPTFILEREFERENCEDATA",
  "MANUALEFFORTDEFAULT",
  "MODELTRAININGFILE",
  "CONTENTUPLOADMODELID",
  "libraryType",
  "AGENTINSTANCEVARIABLEDEFINITION",
  "PROJECTINSTANCEVARIABLEDEFINITION",
  "PROCESSACTION",
  "WORKSPACEID",
  "AUTONOMOUSTRIGGERDATA",
  "MULTIINPUT",
  "COMPLETEDONID",
  "EXECUTIONEVENTTYPE",
  "INSTRUCTIONFORINSTANCESETUP",
  "TRIGGEREVENTTYPEARR",
  "ALLOWAIRESPONSERECEIVER",
  "LLMREASONINGDEPTH",
  "LLMRESPONSEDETAILS",
  "llmActionToolArr",
  "LLMSTRUCTUREDOUTPUT",
  "LLMSTRUCTUREDOUTPUTFORMAT",
  "flightVariablesPromptArr",
  "solutionRoleTitlesPromptArr"
];

export const defaultPromptObjectMapping: DefaultPromptObjectMapping = {
  [PROMPT_TYPE.STANDARD_PROMPT]: {
    S: 'promptActorDefinition',
    A: 'promptActorDefinition',
    E: 'promptActorDefinition',
  },
  [PROMPT_TYPE.CHART_PROMPT]: {
    S: 'promptActorDefinition',
    A: 'promptActorDefinition',
    E: 'promptActorDefinition', 
  },
  [PROMPT_TYPE.CONTENT_UPLOAD]: {
    S: 'promptDAction',
    A: 'promptDAction',
    E: 'promptDAction',
  },
  [PROMPT_TYPE.AGENT_VARIABLE_DEFINITION]: {
    S: 'promptDAction',
    A: 'promptDAction',
    E: 'promptDAction',
  },
  [PROMPT_TYPE.ITERATIVE_PROMPT]: {
    S: 'promptDAction',
    A: 'promptDAction',
    E: 'promptDAction',
  },
  [PROMPT_TYPE.INPUT_DUAL_PROMPT]: {
    S: 'promptDAction',
    A: 'promptDAction',
    E: 'promptDAction',
  },
  [PROMPT_TYPE.SURVEY_PROMPT]: {
    S: 'promptSurveyPrompt',
    A: 'promptSurveyPrompt',
    E: 'promptSurveyPrompt',
  },
  [PROMPT_TYPE.LOOP_PROMPT]: {
    S: 'promptActorDefinition',
    A: 'promptActorDefinition',
    E: 'promptActorDefinition',
  },
  [PROMPT_TYPE.HYBRID_PROMPT]: {
    S: 'promptActorDefinition',
    A: 'promptActorDefinition',
    E: 'promptActorDefinition',
  },
  [PROMPT_TYPE.RESPONSE_COMPILATION_PROMPT]: {
    S: 'promptProcessAction',
    A: 'promptProcessAction',
    E: 'promptProcessAction',
  },
  [PROMPT_TYPE.FLIGHT_VARIABLE_DEFINITION]: {
    S: 'promptDAction',
    A: 'promptDAction',
    E: 'promptDAction',
  },
};
